import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Copy, Check, Loader2, Clock, User, Mail } from 'lucide-react';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiKeysApi, ApiKeyListItem, ApiKeyCreatedResponse } from '@/api/api-keys';
import { useAuth } from '@/auth';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Никогда';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ApiKeyCard({
  apiKey,
  onRevoke,
  isRevoking,
}: {
  apiKey: ApiKeyListItem;
  onRevoke: () => void;
  isRevoking: boolean;
}) {
  const isRevoked = !!apiKey.revokedAt;

  return (
    <div
      className={`bg-card rounded-lg border border-border p-4 ${
        isRevoked ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`font-medium ${isRevoked ? 'line-through' : ''}`}>
            {apiKey.name}
          </p>
          <div className="mt-1 space-y-1 text-sm text-muted-foreground">
            <p className="font-mono text-xs bg-muted px-2 py-0.5 rounded inline-block">
              {apiKey.keyPrefix}...
            </p>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {isRevoked
                ? `Отозван ${formatDate(apiKey.revokedAt)}`
                : `Использован: ${formatDate(apiKey.lastUsedAt)}`}
            </div>
          </div>
        </div>
        {!isRevoked && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRevoke}
            disabled={isRevoking}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            {isRevoking ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Trash2 className="w-5 h-5" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

export function ProfilePage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<ApiKeyCreatedResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const { data: keys, isLoading, error } = useQuery({
    queryKey: ['api-keys'],
    queryFn: apiKeysApi.getMyKeys,
  });

  const createMutation = useMutation({
    mutationFn: apiKeysApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setIsCreateOpen(false);
      setNewKeyName('');
      setCreatedKey(data);
      toast.success('Ключ создан');
    },
    onError: () => {
      toast.error('Ошибка создания ключа');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: apiKeysApi.revoke,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setRevokingId(null);
      toast.success('Ключ отозван');
    },
    onError: () => {
      setRevokingId(null);
      toast.error('Ошибка отзыва ключа');
    },
  });

  const handleCreate = () => {
    if (!newKeyName.trim()) return;
    createMutation.mutate({ name: newKeyName.trim() });
  };

  const handleRevoke = (key: ApiKeyListItem) => {
    if (!confirm(`Отозвать ключ "${key.name}"?`)) return;
    setRevokingId(key.id);
    revokeMutation.mutate(key.id);
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      toast.success('Скопировано в буфер обмена');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  const activeKeys = keys?.filter((k) => !k.revokedAt) || [];
  const revokedKeys = keys?.filter((k) => k.revokedAt) || [];

  return (
    <div className="min-h-screen bg-background">
      <Header title="Профиль" showBack />

      <main className="p-4 space-y-6">
        {/* Информация о пользователе */}
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-lg truncate">
                {user?.name || user?.username || 'Пользователь'}
              </p>
              {user?.email && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  {user.email}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* API ключи */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              <h2 className="font-semibold">API ключи</h2>
            </div>
            <Button size="sm" onClick={() => setIsCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Создать
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Загрузка...
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              Ошибка загрузки ключей
            </div>
          ) : activeKeys.length === 0 && revokedKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>У вас пока нет API ключей</p>
              <p className="text-sm mt-1">
                Создайте ключ для подключения мобильного приложения
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeKeys.map((key) => (
                <ApiKeyCard
                  key={key.id}
                  apiKey={key}
                  onRevoke={() => handleRevoke(key)}
                  isRevoking={revokingId === key.id}
                />
              ))}

              {revokedKeys.length > 0 && (
                <div className="pt-4">
                  <p className="text-sm text-muted-foreground mb-2">
                    Отозванные ключи
                  </p>
                  <div className="space-y-2">
                    {revokedKeys.map((key) => (
                      <ApiKeyCard
                        key={key.id}
                        apiKey={key}
                        onRevoke={() => {}}
                        isRevoking={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Диалог создания ключа */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Создать API ключ</DialogTitle>
            <DialogDescription>
              Введите название для нового ключа (например, название устройства)
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Например: Samsung Galaxy"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!newKeyName.trim() || createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог с созданным ключом */}
      <Dialog open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-5 h-5" />
              Ключ создан
            </DialogTitle>
            <DialogDescription>
              Скопируйте ключ сейчас. После закрытия этого окна вы не сможете
              увидеть его снова!
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-mono break-all">
              {createdKey?.key}
            </code>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyKey}
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedKey(null)}>Готово</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
